import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance, axPerfLogger } from '@theme/performance';
import { getCartManager } from '@theme/cart-manager';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 * @property {HTMLElement} [cartLiveRegion] - The live region for accessibility announcements.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** @type {boolean} Flag: cart has only special products, needs full clear */
  #needToAllDelete = false;

  /** @type {boolean} Flag: cart contains special products that may need removal */
  #needToSpecialProductsDelete = false;

  /** @type {string[]} Special product properties (e.g. ['_gift_wrap', '_sample', '_Gifted']) */
  #specialProperties = [];

  connectedCallback() {
    super.connectedCallback();

    // Read Tediber-pattern data attributes set by Liquid
    this.#needToAllDelete = this.dataset.needToAllDelete === 'true';
    this.#needToSpecialProductsDelete = this.dataset.needToSpecialProductsDelete === 'true';
    if (this.#needToSpecialProductsDelete) {
      this.#specialProperties = (this.dataset.properties || '').split(',').filter(Boolean);
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    // Server-side flag: if cart has ONLY special products, clear them immediately
    // Skip in theme editor to avoid clearing the merchant's test cart on every section refresh
    if (this.#needToAllDelete && !Shopify.designMode) {
      this.#clearAllItems();
    }
  }

  /**
   * Called by the morph after Section Rendering.
   * Re-reads dataset flags that may have changed (e.g. special products state).
   */
  updatedCallback() {
    super.updatedCallback();

    this.#needToAllDelete = this.dataset.needToAllDelete === 'true';
    this.#needToSpecialProductsDelete = this.dataset.needToSpecialProductsDelete === 'true';
    if (this.#needToSpecialProductsDelete) {
      this.#specialProperties = (this.dataset.properties || '').split(',').filter(Boolean);
    }

    if (this.#needToAllDelete && !Shopify.designMode) {
      this.#clearAllItems();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  // ─── Centralized Cart Operations ────────────────────────

  /**
   * @typedef {Object} CartResponse
   * @property {Record<string, string>} [sections] - Section HTML keyed by section ID.
   * @property {Array<{variant_id: number, quantity: number, key: string}>} [items] - Cart line items.
   * @property {number} [item_count] - Total item count.
   * @property {string} [errors] - Error message from Shopify.
   */

  /**
   * Centralizes all cart API operations (add/change/update/clear).
   * Mirrors Tediber's processCartOperation pattern:
   * - `add`: updates cart state and returns early (no quantity validation / focus management)
   * - `change` / `update` / `clear`: full flow with error handling, validation, focus, events
   *
   * @param {Object} params
   * @param {'add' | 'change' | 'update' | 'clear'} params.operation - The cart operation.
   * @param {Object} params.data - Operation-specific data (line, quantity, updates, etc.).
   * @param {Object} [params.options]
   * @param {string|string[]} [params.options.keys=[]] - Cart item key(s) for error handling & quantity validation.
   * @param {number} [params.options.line] - Line index (1-based) for inline error display via refs.
   * @param {string} [params.options.name] - Active element name for focus management after update.
   * @param {string} [params.options.variantId] - Variant ID included in the dispatched CartUpdateEvent.
   * @param {boolean} [params.options.refreshCart=true] - Whether to morph the section and dispatch events.
   * @returns {Promise<CartResponse>} The parsed API response.
   */
  async processCartOperation({ operation, data, options = {} }) {
    const { keys = [], line = undefined, name = undefined, variantId = undefined, refreshCart = true } = options;

    const endpoints = {
      add: Theme.routes.cart_add_url,
      change: Theme.routes.cart_change_url,
      update: Theme.routes.cart_update_url,
      clear: '/cart/clear.js',
    };

    const endpoint = endpoints[operation];
    if (!endpoint) throw new Error(`Invalid cart operation: ${operation}`);

    const perfOp = axPerfLogger.start(`cart:${operation}`, { data, options });

    try {
      this.#disableCartItems();
      this.#disableCheckoutButton();
      perfOp.mark('ui-disabled');

      const body = JSON.stringify(this.#prepareCartData(data));
      perfOp.mark('fetch-start');
      const response = await fetch(endpoint, fetchConfig('json', { body }));
      perfOp.mark('fetch-response');
      const responseData = await response.json();
      perfOp.mark('json-parsed');

      // ── Add: update state & publish event, return early (no validation / focus) ──
      if (operation === 'add') {
        this.#updateCartState(responseData, refreshCart);
        perfOp.mark('state-updated');
        this.#publishCartUpdateEvent(responseData, variantId);
        perfOp.mark('event-dispatched');
        return responseData;
      }

      // ── Change / Update / Clear: full handleCartUpdate flow ──
      await this.#handleOperationResponse(responseData, { keys, line, name, variantId, refreshCart });
      perfOp.mark('response-handled');

      return responseData;
    } catch (error) {
      console.error(`Error processing cart ${operation}:`, error);
      throw error;
    } finally {
      this.#enableCartItems();
      this.#enableCheckoutButton();
      perfOp.mark('ui-enabled');
      perfOp.end();
    }
  }

  /**
   * Full post-operation handler for change/update/clear.
   * Mirrors Tediber's handleCartUpdate: errors → state → validate quantities → live regions → focus → events.
   *
   * @param {CartResponse} response
   * @param {Object} opts
   * @param {string|string[]} opts.keys
   * @param {number} [opts.line] - Line index (1-based) for inline error display.
   * @param {string} [opts.name]
   * @param {string} [opts.variantId]
   * @param {boolean} opts.refreshCart
   */
  async #handleOperationResponse(response, { keys, line, name, variantId, refreshCart }) {
    if (response.errors) {
      this.#handleCartError(keys, response.errors, line);
      return;
    }

    // 1. Update cart state (morph, toggle empty, dispatch event)
    this.#updateCartState(response, refreshCart);

    // 2. Per-key quantity validation + live region announcements
    const lineItems = Array.isArray(keys) ? keys : [keys];
    for (const key of lineItems) {
      if (!key) continue;
      const message = this.#validateQuantityUpdate(key, response);
      this.#updateLiveRegionByKey(key, message);
    }

    // 3. Focus management (keep focus inside drawer after DOM morph)
    this.#handleFocusManagement(keys, name, response);

    // 4. Publish cart update event with variantId
    this.#publishCartUpdateEvent(response, variantId);
  }

  /**
   * Updates cart state: toggle empty classes, morph section, dispatch CartUpdateEvent.
   * For `add` this is the only handler; for other ops it's called inside #handleOperationResponse.
   *
   * @param {CartResponse} response
   * @param {boolean} refreshCart
   */
  #updateCartState(response, refreshCart) {
    if (response.errors) return;
    if (!refreshCart) return;

    const sectionHtml = response.sections?.[this.sectionId];
    // /cart/add.js returns only the added lines (no item_count): read the full cart instead.
    const isEmpty = (response.item_count ?? getCartManager().cartJSON?.item_count ?? 0) === 0;
    const wasEmpty = !this.querySelector('.cart-form');

    // Toggle empty state on cart elements
    this.classList.toggle('is-empty', isEmpty);
    document.querySelector('cart-drawer-component')?.classList.toggle('is-empty', isEmpty);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', isEmpty);

    if (sectionHtml) {
      this.#updateQuantitySelectors(response);

      // Pre-morph: remove rows currently animating out so morph doesn't pair
      // them against the new SSR row positions (would cause remaining rows
      // to disappear during remove animations).
      for (const removingRow of this.querySelectorAll('.cart-items__table-row.removing')) {
        removingRow.remove();
      }

      // Full morph for empty↔non-empty transitions (DOM structure completely different).
      // Hydration morph for non-empty→non-empty (only keyed zones update, upsell preserved).
      const isStableState = !wasEmpty && !isEmpty;
      const morphMode = this.isDrawer && isStableState ? 'hydration' : 'full';
      morphSection(this.sectionId, sectionHtml, { mode: morphMode, injectStylesheet: true });
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  }

  /**
   * Prepares the request payload by appending sections metadata.
   * @param {Object} data - Operation-specific data.
   * @returns {Object} Data enriched with sections info.
   */
  #prepareCartData(data) {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    }

    return {
      ...data,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };
  }

  /**
   * Validates whether the requested quantity update was actually applied by Shopify.
   * Shopify silently caps quantities to available stock — this detects the discrepancy.
   *
   * @param {string} key - Cart item key
   * @param {CartResponse} response
   * @returns {string} Warning message (empty if no issue)
   */
  #validateQuantityUpdate(key, response) {
    if (!response.items) return '';

    const lineItemRow = this.querySelector(`[data-key="${key}"]`);
    const quantityInput = lineItemRow?.querySelector('input[ref="quantityInput"]');
    const item = response.items.find((i) => i.key === key);
    const updatedValue = item?.quantity;
    const currentValue = quantityInput ? parseInt(/** @type {HTMLInputElement} */ (quantityInput).value, 10) : 0;

    if (quantityInput && updatedValue !== undefined && updatedValue !== currentValue) {
      const msg = /** @type {string} */ (
        updatedValue === 1 ? (Theme.strings?.quantityErrorOne ?? '') : (Theme.strings?.quantityErrorOther ?? '')
      );
      return msg ? msg.replace('[quantity]', `${updatedValue}`) : '';
    }

    return '';
  }

  /**
   * Manages focus after a cart update to keep focus inside the drawer.
   * Finds the element by name inside the affected row, or falls back to first cart item.
   *
   * @param {string|string[]} keys - Cart item key(s)
   * @param {string | undefined} name - Element name attribute to focus
   * @param {CartResponse} response
   */
  #handleFocusManagement(keys, name, response) {
    if (!name) return;

    const firstKey = Array.isArray(keys) ? keys[0] : keys;
    if (!firstKey) return;

    const lineItem = this.querySelector(`[data-key="${firstKey}"]`);
    const target = lineItem?.querySelector(`[name="${name}"]`);

    if (target instanceof HTMLElement) {
      target.focus();
    } else if (response.item_count === 0) {
      // Cart is now empty — focus the empty state
      const emptyLink = this.querySelector('.cart__login-paragraph a, .cart-empty a');
      if (emptyLink instanceof HTMLElement) emptyLink.focus();
    }
  }

  /**
   * Handles cart errors by reverting input values and displaying inline error messages.
   * Supports both key-based (via data-key) and line-based (via refs) error display.
   *
   * @param {string|string[]} keys - Cart item key(s)
   * @param {string} errors - Error message
   * @param {number} [line] - Line index (1-based) for inline error display via refs.
   */
  #handleCartError(keys, errors, line) {
    // Revert quantity inputs by key
    const lineItems = Array.isArray(keys) ? keys : [keys];
    for (const key of lineItems) {
      if (!key) continue;
      const lineItemRow = this.querySelector(`[data-key="${key}"]`);
      const quantityInput = lineItemRow?.querySelector('input[ref="quantityInput"]');
      if (quantityInput instanceof HTMLInputElement) {
        quantityInput.value = quantityInput.defaultValue;
      }
    }

    // Display inline error via line-based refs (cartItemError-1, cartItemErrorContainer-1, etc.)
    if (line) {
      const cartItemError = this.refs[`cartItemError-${line}`];
      const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

      if (cartItemErrorContainer instanceof HTMLElement) {
        cartItemErrorContainer.classList.remove('hidden');
      }
      if (cartItemError instanceof HTMLElement) {
        cartItemError.textContent = errors;
      }
    }

    // Also update by key for accessibility
    for (const key of lineItems) {
      if (key) this.#updateLiveRegionByKey(key, errors);
    }
  }

  /**
   * Updates live regions for a cart item identified by key.
   * @param {string} key - Cart item key
   * @param {string} message - Message to announce (empty = clear error)
   */
  #updateLiveRegionByKey(key, message) {
    const lineItemRow = this.querySelector(`[data-key="${key}"]`);
    if (!lineItemRow) return;

    const errorContainer = lineItemRow.querySelector('[ref^="cartItemErrorContainer"]');
    const errorElement = lineItemRow.querySelector('[ref^="cartItemError-"]');

    if (errorContainer instanceof HTMLElement) {
      if (message) {
        if (errorElement instanceof HTMLElement) errorElement.textContent = message;
        errorContainer.classList.remove('hidden');
      } else {
        errorContainer.classList.add('hidden');
      }
    }
  }

  /**
   * Dispatches a CartUpdateEvent with optional variantId.
   * Separated from #updateCartState so `add` can skip it.
   *
   * @param {CartResponse} response
   * @param {string} [variantId]
   */
  #publishCartUpdateEvent(response, variantId) {
    const sectionHtml = response.sections?.[this.sectionId];
    if (!sectionHtml) return;

    const newSectionHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const itemCount = parseInt(
      newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent || '0',
      10
    );

    this.dispatchEvent(
      new CartUpdateEvent(response, this.sectionId, {
        itemCount,
        source: 'cart-items-component',
        sections: response.sections,
        ...(variantId && { variantId }),
      })
    );
  }

  /**
   * Clears the entire cart when it contains only special products.
   * Triggered by the server-side `data-need-to-all-delete` flag.
   */
  async #clearAllItems() {
    try {
      await this.processCartOperation({ operation: 'clear', data: {} });
    } catch (error) {
      console.error('Error clearing special-only cart:', error);
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    for (const textComponent of lineItemRow.querySelectorAll('text-component')) {
      /** @type {TextComponent} */ (textComponent).shimmer();
    }
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    // (counted on cart item rows only — `rowsToAnimate` below adds presentational rows).
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    // The Withings+ selector under a device is a separate presentational `<tr>`
    // keyed `<device key>-sub`, outside `cartItemRows`. Left out, it would only
    // disappear with the morph (~450ms after its device row), so the removal read
    // as two jerky steps. Animate it out with its device row instead.
    const rowsToAnimate = [
      ...rowsToRemove,
      ...rowsToRemove.flatMap((row) =>
        row.dataset.key
          ? Array.from(this.querySelectorAll(`.cart-items__subscription-row[data-key="${row.dataset.key}-sub"]`))
          : []
      ),
    ];

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      // For the drawer: animate the last row out (like non-last items),
      // THEN swap the empty template in. This achieves two things at once:
      //
      // 1. Visual continuity — the item fades out the same way as removing
      //    one of many items, instead of an instant flash to empty state.
      // 2. No "close + reopen" flash — by the time the swap runs (after
      //    the row animation), the click event has long finished bubbling.
      //    No risk of the dialog's `#handleClick` seeing the Remove button
      //    detached from the DOM and (wrongly) calling `closeDialog()`.
      if (this.isDrawer) {
        const lastRow = rowsToRemove[0];
        if (!lastRow || prefersReducedMotion()) {
          // No row to animate, or user prefers no motion — still defer one
          // frame so the click bubble completes before the DOM swap.
          requestAnimationFrame(() => this.replaceChildren(clone));
          return;
        }
        // Animate the device row AND its Withings+ selector, then swap in the
        // empty state once the (identically timed) collapse is done.
        rowsToAnimate.forEach((row) => {
          row.style.setProperty('--row-height', `${row.clientHeight}px`);
          row.classList.add('removing');
        });
        // `subtree: false`: see the note in the sibling call below (infinite spinner).
        onAnimationEnd(lastRow, () => this.replaceChildren(clone), { subtree: false });
        return;
      }

      // On the main cart page, fall back to the view-transition or Firefox
      // crossfade (these contexts don't have the modal-dialog gotchas).
      if (navigator.userAgent.includes('Firefox')) {
        this.#crossfadeToEmptyState(clone);
        return;
      }

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, ['empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToAnimate.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends. `subtree: false` is required:
      // `.removing::after` carries an infinite spinner, and a subtree wait would
      // never settle (its `finished` promise never resolves) — the row would
      // then linger until the morph.
      onAnimationEnd(row, remove, { subtree: false });
    });
  }

  /**
   * Fallback animation used when View Transitions API is unavailable (Firefox).
   * Swap instantané puis animation transform-only : on ne touche JAMAIS
   * l'opacité, sinon le fond blanc du dialog transparait pendant l'animation.
   * @param {DocumentFragment} clone
   */
  #crossfadeToEmptyState(clone) {
    this.replaceChildren(clone);

    if (prefersReducedMotion()) return;

    this.animate(
      [
        { transform: 'translateY(12px) scale(0.98)' },
        { transform: 'translateY(0) scale(1)' },
      ],
      { duration: 280, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    );
  }

  /**
   * Updates the quantity of a single line item.
   * @param {Object} config - The config.
   * @param {number} config.line - The line (1-indexed, for Shopify API).
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const marker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const { line, quantity } = config;

    // Resolve the cart item key from the row for post-response handling
    const lineItemRow = this.refs.cartItemRows[line - 1];
    const key = lineItemRow?.dataset.key;

    // Shimmer effect while loading
    this.refs.cartTotal?.shimmer();

    try {
      await this.processCartOperation({
        operation: 'change',
        data: { line, quantity },
        options: {
          keys: key ? [key] : [],
          line,
          name: 'quantity',
        },
      });

      resetShimmer(this);
    } catch (error) {
      console.error(error);
    } finally {
      cartPerformance.measureFromMarker(marker);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    // Ignore failed add-to-cart — no sections to morph, avoid unnecessary re-render
    if (event.detail?.data?.didError) return;

    // Validate cart conditions only from cart-manager (guaranteed full cart data)
    // Theme-originated events (product form, cart-items) may have partial data
    if (event.detail?.data?.source === 'cart-manager') {
      const resource = /** @type {{ items?: Array<{key: string, properties: Record<string, string>}> }} */ (event.detail?.resource);
      if (resource?.items) {
        this.checkCartConditions(resource.items);
      }
    }

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      // Pre-morph cleanup: remove rows currently animating out (`.removing`)
      // before morph compares old vs new. Otherwise the morph sees the live
      // DOM with the row still present (height 0, mid-animation) and matches
      // it against the new SSR row positionally — both rows end up swapped
      // and the remaining product disappears. Removing them here is safe:
      // `onAnimationEnd` would have removed them anyway once the animation
      // completed; we just pre-empt it to keep DOM ↔ SSR aligned.
      for (const removingRow of this.querySelectorAll('.cart-items__table-row.removing')) {
        removingRow.remove();
      }

      // Full morph for empty↔non-empty transitions (DOM structure completely different).
      // Hydration morph for non-empty→non-empty (only keyed zones update, upsell preserved).
      const wasEmpty = !this.querySelector('.cart-form');
      const newDoc = new DOMParser().parseFromString(cartItemsHtml, 'text/html');
      const newIsEmpty = !newDoc.querySelector('.cart-form');
      const isStableState = !wasEmpty && !newIsEmpty;
      const morphMode = this.isDrawer && isStableState ? 'hydration' : 'full';
      morphSection(this.sectionId, cartItemsHtml, { mode: morphMode, injectStylesheet: true });

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  // ─── Checkout Button State (Chantier 3) ──────────────────

  /**
   * Disables checkout buttons during cart operations to prevent
   * submitting a cart in a transitional state.
   */
  #disableCheckoutButton() {
    for (const btn of document.querySelectorAll('.cart__checkout-button')) {
      btn.setAttribute('aria-disabled', 'true');
    }
  }

  /**
   * Re-enables checkout buttons after cart operations complete.
   */
  #enableCheckoutButton() {
    for (const btn of document.querySelectorAll('.cart__checkout-button')) {
      btn.setAttribute('aria-disabled', 'false');
    }
  }

  // ─── Cart Validation — Special Products (Chantier 4) ────

  /**
   * Validates cart state after updates. If the cart contains ONLY special products
   * (gift wrap, samples, gifted items), auto-removes them.
   * Called after each cart update via the cart-manager interception.
   * @param {Array<{key: string, properties: Record<string, string>}>} [items] - Cart items from the API response
   */
  async checkCartConditions(items) {
    if (this.#specialProperties.length === 0) return;
    if (!items?.length) return;

    const hasNormalProduct = items.some(
      (item) => !this.#specialProperties.some((prop) => item.properties?.[prop] === 'true')
    );

    if (!hasNormalProduct) {
      await this.#removeSpecialProducts(items);
    }
  }

  /**
   * Removes all special products from the cart.
   * @param {Array<{key: string, properties: Record<string, string>}>} items
   */
  async #removeSpecialProducts(items) {
    /** @type {Record<string, number>} */
    const updates = {};
    for (const item of items) {
      if (this.#specialProperties.some((prop) => item.properties?.[prop] === 'true')) {
        updates[item.key] = 0;
      }
    }

    if (Object.keys(updates).length === 0) return;

    try {
      await this.processCartOperation({ operation: 'update', data: { updates } });
    } catch (error) {
      console.error('Error removing special products:', error);
    }
  }

  // ─── Multi-item Quantity Updates — Bundles (Chantier 5) ──

  /**
   * Updates the quantity of multiple linked cart items in a single request.
   * Finds all items sharing a common property (e.g. _bundle_id) and updates them together.
   * @param {number} quantity - New quantity
   * @param {{ referenceKey: string, referenceId: string, currentKey?: string }} options
   */
  async updateMultipleQuantity(quantity, options) {
    const { referenceKey, referenceId, currentKey } = options;
    if (!referenceKey || !referenceId) return;

    try {
      // Fetch current cart to find linked items
      const response = await fetch(Theme.routes.cart_url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      const cart = await response.json();

      if (!cart?.items?.length) return;

      /** @type {Record<string, number>} */
      const updates = {};

      if (currentKey) {
        updates[currentKey] = quantity;
      }

      for (const item of cart.items) {
        const propValue = /** @type {Record<string, string>} */ (item.properties)?.[referenceKey];
        if (propValue && parseInt(propValue) === parseInt(referenceId)) {
          updates[item.key] = quantity;
        }
      }

      await this.processCartOperation({ operation: 'update', data: { updates } });
    } catch (error) {
      console.error('Error in updateMultipleQuantity:', error);
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
